// src/app/components/MultiplacerCeilingDevices/MultiplacerCeilingDevices.tsx
import React, { useMemo, useRef } from "react";
import { Card, Box } from "@mantine/core";
import Resizable from "react-resizable-layout";

import MultplacerCeilingDevicesTable from "./MultplacerCeilingDevicesTable";
import { MultiplacerCeilingDevicesToolBar } from "./MultiplacerCeilingDevicesToolBar";
import SingleDevice from "./SingleDevice";
import { useAppSelector } from "../../app/hooks";
import {
  selectActiveDeviceIsAirTerminal,
  selectDevicesExistInRoom,
} from "../../DesignTab/devices/deviceSlice";
import { EmptyRoomDevicesState } from "./EmptyRoomDevicesState";

const SPLIT_STORAGE_KEY = "ceilingDevicesSplitY";

const MultiplacerCeilingDevices: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // ❗ initial size från localStorage
  const initialPosition = useMemo(() => {
    if (typeof window === "undefined") return 260;

    const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;

    if (!Number.isFinite(n) || n < 120) return 260;
    return n;
  }, []);

  const devicesExistInRoom = useAppSelector(selectDevicesExistInRoom);

  return (
    <Card withBorder radius="md" w="100%" h="100%" p={0}>
      {/* Top toolbar */}
      <Card.Section inheritPadding py="xs" style={{ marginTop: "-8px" }}>
        <MultiplacerCeilingDevicesToolBar />
        <hr />
      </Card.Section>

      {/* Resizeable region */}
      <Box ref={containerRef} style={{ height: "calc(100% - 60px)" }}>
        <Resizable
          axis="y"
          containerRef={containerRef}
          initial={initialPosition}
          min={120}
          onResizeEnd={(pos) => window.localStorage.setItem(SPLIT_STORAGE_KEY, String(pos))}
        >
          {({ position, separatorProps }) => (
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Övre panel: tabell */}
              <div
                style={{
                  height: position,
                  minHeight: 100,
                  overflow: "auto",
                  padding: 12,
                }}
              >
                {devicesExistInRoom ? <MultplacerCeilingDevicesTable /> : <EmptyRoomDevicesState />}
              </div>

              {/* Separator */}
              <div {...separatorProps} style={{ flex: "0 0 auto" }}>
                <div
                  style={{
                    height: 6,
                    cursor: "row-resize",
                    background: "linear-gradient(to bottom, #e0e0e0, #c8c8c8, #e0e0e0)",
                    borderRadius: 4,
                  }}
                />
              </div>

              {/* Nedre panel: One selected device */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  padding: 12,
                  borderTop: "1px solid #ddd",
                  background: "#fafafa",
                  overflow: "auto",
                }}
              >
                <SingleDevice />
              </div>
            </div>
          )}
        </Resizable>
      </Box>
    </Card>
  );
};

export default MultiplacerCeilingDevices;
